import { ComposableSearchDropdown, ComposableSearchDropdownProps } from "@themuse/design-system";
import { IndexedObject } from "@themuse/design-system/lib/components/Dropdowns/ComposableDropdown/DropdownProvider";
import { LocationShape } from "@themuse/design-system/lib/components/Icons";
import { UseComboboxStateChange } from "downshift"; // eslint-disable-line import/no-extraneous-dependencies
import React from "react";
import { SearchLocation } from "interfaces/context/searchContextInterfaces";
import { countries, suggestionApiQueryMaxlength } from "utils/constants";
import homeStyles from "styles/Home.module.scss";
import styles from "./SearchInput.module.scss";

interface SearchInputFieldProps {
    clear?: boolean;
    onClear?: () => void;
}

interface LocationSearchFieldProps extends SearchInputFieldProps {
    location: SearchLocation;
    onInputChange: (changes: UseComboboxStateChange<IndexedObject>) => void;
    noResultsMessage: React.ReactNode;
}

const menuToggle = (value: boolean) => {
    const navHeader = document.querySelectorAll("header[class^='Header_header']");

    navHeader.forEach((elem) => {
        if (value) {
            elem.classList.add(homeStyles.inputMenuOpen);
        } else {
            elem.classList.remove(homeStyles.inputMenuOpen);
        }
    });
};

/**
 * Determines if a given label corresponds to a country-level location.
 */
const isCountryLocation = (label: string): boolean => !!label && countries.includes(label);

/**
 * Filters a list of locations based on a search value and excludes country-level locations.
 */
const filterList = (list: IndexedObject[], value = "") => list
    // Filter by search value if provided
    .filter((item: IndexedObject) => item?.label?.toLowerCase().includes(value?.toLowerCase()))
    // Do not include country level searches.
    .filter((item: IndexedObject) => !isCountryLocation(item?.label));

/**
 * Renders a search input field with a dropdown for selecting locations. It utilizes ComposableSearchDropdown.
 */
const LocationSearchInput = (props: LocationSearchFieldProps) => {
    const {
        location, clear, onClear, onInputChange, noResultsMessage
    } = props;
    const { locationSuggestions, userSelectedLocation, userInputValue } = location;
    const placeHolderText = "Anywhere";
    const dropdownProps: ComposableSearchDropdownProps = {
        classes: {
            container: styles.searchInputContainer,
            inputIcon: styles.inputIcon
        },
        dropdownProps: {
            dropdownList: filterList(locationSuggestions, userInputValue),
            IconComponent: <LocationShape />,
            dropdownTitle: "Type to select a location",
            debounceWait: 200,
            labelText: "Locations",
            placeHolderText,
            inputId: "location-input",
            maxLength: suggestionApiQueryMaxlength,
            onInputChange,
            onMenuToggle: menuToggle,
            clearValue: clear,
            onClear,
            fieldValue: userSelectedLocation,
            showMenuOnFocus: true,
            refocusMobileOnClear: true,
            noResultsMessage
        }
    };

    return <ComposableSearchDropdown {...dropdownProps} />;
};

LocationSearchInput.displayName = "LocationSearchInput";
export default LocationSearchInput;
